/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import MsDyn365, { IRequestContextUrl, sendRequest, RichTextComponent, ContentEditableEvent } from '@msdyn365-commerce/core';

import { BusinessPartner, BusinessPartnersDataActions } from '@msdyn365-commerce/retail-proxy';
import { IModuleProps, Heading, Alert } from '@msdyn365-commerce-modules/utilities';
import { ISearchBarOption } from '@msdyn365-commerce/components';

import { IOrganizationSelectionData } from './organization-selection.data';
import { IOrganizationSelectionProps } from './organization-selection.props.autogenerated';
import { OrganizationsTable } from './components/organizations-table';

export interface IOrganizationSelectionState {
    currentAccountNumber?: string;
    errorMessage: string;
    filteredOrganizationList?: BusinessPartner[];
    isMobile?: boolean;
    isModalOpen?: boolean;
    loaded: boolean;
    organizationList: BusinessPartner[];
    selectedOrganization?: BusinessPartner;
    skipSwitchOrganizationMessageSelected?: boolean;
}

export interface IOrganizationSelectionViewProps
    extends IOrganizationSelectionProps<IOrganizationSelectionData & { options: ISearchBarOption[] }> {
    cookieConsentNotGivenErrorMessage?: React.ReactNode;
    errorMessage?: string | null;
    heading?: React.ReactNode;
    isModalOpen?: boolean;
    loaded?: boolean;
    moduleClassName?: string;
    moduleContainer?: IModuleProps;
    organizationList?: BusinessPartner[];
    organizationsTable?: React.ReactNode;
    skipSwitchOrganizationMessageSelected?: boolean;
    organizationSelectionWarningContentProps?: React.ReactNode;
    onSwitchOrganizationContinued?: () => Promise<void>;
    searchOrganization?: (text?: string) => void;
    toggleModal?: (action: 'open' | 'close', organization?: BusinessPartner) => void;
    toggleSkipSwitchOrganizationMessage?: () => void;
}

type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 *
 * OrganizationSelection component
 * @extends {React.Component<IOrganizationSelectionProps<IOrganizationSelectionData>, IOrganizationSelectionState>}
 */
class OrganizationSelection extends React.Component<IOrganizationSelectionProps<IOrganizationSelectionData>, IOrganizationSelectionState> {
    private readonly _moduleClassName: string;
    private readonly _organizationSelectionCookieKey: string;
    private readonly _skipSwitchOrganizationMessageLocalStorageKey: string;

    constructor(props: IOrganizationSelectionProps<IOrganizationSelectionData>) {
        super(props);

        this._moduleClassName = 'ms-organization-selection';
        this._organizationSelectionCookieKey = '_msdyn365___org_selection_CAN';
        this._skipSwitchOrganizationMessageLocalStorageKey = '_msdyn365__skip_switch_organization_message_';

        this._onSwitchOrganizationContinued = this._onSwitchOrganizationContinued.bind(this);
        this._searchOrganization = this._searchOrganization.bind(this);
        this._selectOrganization = this._selectOrganization.bind(this);
        this._toggleModal = this._toggleModal.bind(this);
        this._toggleSkipSwitchOrganizationMessage = this._toggleSkipSwitchOrganizationMessage.bind(this);

        this.state = {
            errorMessage: '',
            loaded: false,
            organizationList: []
        };
    }

    public componentDidMount(): void {
        void this._loadOrganizations();

        if (MsDyn365.isBrowser && window) {
            this.setState({
                skipSwitchOrganizationMessageSelected:
                    window.localStorage.getItem(this._skipSwitchOrganizationMessageLocalStorageKey) === 'true'
            });
            window.addEventListener('resize', this._updateViewport);
            this._updateViewport();
            this._setCurrentAccountNumber();
        }
    }

    public componentWillUnmount(): void {
        if (MsDyn365.isBrowser && window.removeEventListener) {
            window.removeEventListener('resize', this._updateViewport);
        }
    }

    public render(): JSX.Element | null {
        const {
            config: { heading, organizationSelectionWarningContent },
            context: {
                request: { cookies }
            },
            resources: { cookieConsentRequiredMessage }
        } = this.props;
        const headingElement =
            heading && Heading ? (
                <Heading className={`${this._moduleClassName}__heading`} headingTag={heading.tag} text={heading.text} />
            ) : null;
        const viewProps = {
            ...this.props,
            cookieConsentNotGivenErrorMessage: !cookies.isConsentGiven() && <Alert color='danger'>{cookieConsentRequiredMessage}</Alert>,
            errorMessage: this.state.errorMessage,
            heading: headingElement,
            isModalOpen: this.state.isModalOpen,
            loaded: this.state.loaded,
            moduleClassName: this._moduleClassName,
            moduleContainer: { moduleProps: this.props },
            organizationList: this.state.organizationList,
            organizationsTable: (
                <OrganizationsTable
                    {...this.props}
                    {...this.state}
                    moduleClassName={this._moduleClassName}
                    organizationSelectionCookieKey={this._organizationSelectionCookieKey}
                    selectOrganization={this._selectOrganization}
                    getCurrentUrl={this._getCurrentUrl}
                    toggleModal={this._toggleModal}
                />
            ),
            skipSwitchOrganizationMessageSelected: this.state.skipSwitchOrganizationMessageSelected,
            organizationSelectionWarningContentProps: organizationSelectionWarningContent && (
                <RichTextComponent
                    className={`${this._moduleClassName}__modal-body-line-1`}
                    {...organizationSelectionWarningContent}
                    text={organizationSelectionWarningContent}
                    editProps={{ onEdit: this.handleWarningTextChange, requestContext: this.props.context.request }}
                />
            ),
            onSwitchOrganizationContinued: this._onSwitchOrganizationContinued,
            searchOrganization: this._searchOrganization,
            toggleModal: this._toggleModal,
            toggleSkipSwitchOrganizationMessage: this._toggleSkipSwitchOrganizationMessage
        };
        return this.props.renderView(viewProps);
    }

    private readonly _loadOrganizations = async (): Promise<void> => {
        const {
            context: { actionContext, telemetry },
            resources: { organizationSelectionErrorMessage }
        } = this.props;

        await BusinessPartnersDataActions.getManagedBusinessPartnersAsync({ callerContext: actionContext })
            .then((organizationList: BusinessPartner[]) => {
                this.setState({
                    organizationList,
                    errorMessage: '',
                    loaded: true
                });
            })
            .catch(error => {
                telemetry.trace(error);
                this.setState({ loaded: true, errorMessage: organizationSelectionErrorMessage });
            });
    };

    private readonly _selectOrganization = async ({ OrganizationCustomerAccountNumber }: BusinessPartner): Promise<void> => {
        const {
            context: {
                actionContext: {
                    requestContext: { url }
                },
                telemetry
            }
        } = this.props;

        const body = {
            cookieKey: this._organizationSelectionCookieKey,
            cookieValue: OrganizationCustomerAccountNumber
        };
        const options = {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true
        };

        await sendRequest(`${this._getCurrentUrl(url).origin}/onerf/cookie`, 'post', body, options)
            .then(() => {
                window.location.href = '/';
            })
            .catch(error => {
                telemetry.trace(error);
            });
    };

    private readonly _searchOrganization = (text?: string): void => {
        const { organizationList } = this.state;
        const searchString = text?.trim()?.toLowerCase() ?? '';
        this.setState({
            filteredOrganizationList: searchString.length
                ? organizationList.filter(({ Name }) => Name?.toLowerCase().includes(searchString))
                : undefined
        });
    };

    private readonly _onSwitchOrganizationContinued = async (): Promise<void> => {
        const { selectedOrganization } = this.state;
        if (selectedOrganization) {
            await this._selectOrganization(selectedOrganization);
        }
        this._toggleModal('close');
    };

    private readonly _toggleSkipSwitchOrganizationMessage = (): void => {
        this.setState(({ skipSwitchOrganizationMessageSelected }) => {
            const nextState = !skipSwitchOrganizationMessageSelected;
            window.localStorage.setItem(this._skipSwitchOrganizationMessageLocalStorageKey, String(nextState));
            return { skipSwitchOrganizationMessageSelected: nextState };
        });
    };

    private readonly _toggleModal = (action: 'open' | 'close', organization?: BusinessPartner): void => {
        const openModal = action === 'open';
        this.setState({ isModalOpen: openModal, selectedOrganization: openModal ? organization : undefined });
    };

    private readonly _updateViewport = (): void => {
        const viewport = this._getViewport();
        this.setState({ isMobile: viewport === 'xs' || viewport === 'sm' });
    };

    private readonly _getViewport = (): GridSize => {
        const {
            context: {
                request: { device, gridSettings }
            }
        } = this.props;

        // Always render in mobile viewport on a mobile device
        if (device.Type === 'Mobile') {
            return 'xs';
        }

        if (MsDyn365.isBrowser && window && gridSettings) {
            const { innerWidth } = window;
            const { lg, md, sm, xs } = gridSettings;

            if (xs && innerWidth <= xs.w) {
                return 'xs';
            }

            if (sm && innerWidth <= sm.w) {
                return 'sm';
            }

            if (md && innerWidth <= md.w) {
                return 'md';
            }

            if (lg && innerWidth <= lg.w) {
                return 'lg';
            }

            return 'xl';
        }

        return 'xs';
    };

    private readonly _setCurrentAccountNumber = () => {
        const { customerAccountNumber } = this.props.context.request.user;
        this.setState({ currentAccountNumber: customerAccountNumber });
    };

    private readonly _getCurrentUrl = ({ requestUrl: { href } }: IRequestContextUrl): URL => {
        if (MsDyn365.isBrowser && typeof window !== 'undefined') {
            const browserHref = window.location?.href;

            if (browserHref) {
                try {
                    return new URL(browserHref);
                } catch {
                    return new URL(browserHref, href);
                }
            }
        }

        return new URL(href);
    };

    private handleWarningTextChange = (event: ContentEditableEvent): void => {
        this.props.config.organizationSelectionWarningContent = event.target.value;
    };
}

export default OrganizationSelection;
